// reCAPTCHA v2 Invisible — programmatic execution
// Docs: https://developers.google.com/recaptcha/docs/invisible#programmatic_execute
//
// Flow:
//   1. grecaptcha.ready() — wait for script to load
//   2. grecaptcha.render(container, { sitekey, size: 'invisible', callback }) — render widget once
//   3. grecaptcha.execute(widgetId) — trigger challenge
//   4. callback receives the token
//   5. grecaptcha.reset(widgetId) — MUST reset after each use so next execute() works

const READY_TIMEOUT_MS = 10_000;

let widgetId: number | null = null;
let pendingResolve: ((token: string) => void) | null = null;

function getKey(): string {
  const key = window.RUNTIME_CONFIG?.CAPTCHA_KEY;
  if (!key) throw new Error('reCAPTCHA: CAPTCHA_KEY is not configured');
  return key;
}

function waitUntilReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('reCAPTCHA: timed out waiting for script to load')),
      READY_TIMEOUT_MS,
    );
    window.grecaptcha.ready(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

function onToken(token: string): void {
  if (widgetId !== null) window.grecaptcha.reset(widgetId);
  pendingResolve?.(token);
  pendingResolve = null;
}

function ensureWidget(key: string): void {
  if (widgetId !== null) return;

  const container = document.createElement('div');
  container.style.display = 'none';
  document.body.appendChild(container);

  widgetId = window.grecaptcha.render(container, {
    sitekey: key,
    size: 'invisible',
    callback: onToken,
    'expired-callback': () => {
      if (widgetId !== null) window.grecaptcha.reset(widgetId);
    },
  });
}

export async function executeCaptcha(): Promise<string> {
  if (typeof window === 'undefined' || !window.grecaptcha) {
    throw new Error('reCAPTCHA: script is not loaded');
  }

  const key = getKey();

  await waitUntilReady();

  ensureWidget(key);

  return new Promise<string>((resolve) => {
    pendingResolve = resolve;
    window.grecaptcha.execute(widgetId!);
  });
}
